import { FormEvent, forwardRef, useImperativeHandle, useState } from 'react';

import { GMetrixCheckInput } from 'components/GMetrixCheckInput/GMetrixCheckInput';
import { GMetrixTabs, GMetrixTabsTab } from 'components/GMetrixTabs/GMetrixTabs';
import { Alert, Col, Container, Form, InputGroup, Row } from 'react-bootstrap';
import { GMetrixDropdown } from 'components/GMetrixDropdown/GMetrixDropdown';
import { GMetrixLoader } from 'components/GMetrixLoader/GMetrixLoader';
import { GMetrixButton } from 'components/GMetrixButton/GMetrixButton';
import { GMetrixDialog } from 'components/GMetrixDialog/GMetrixDialog';
import { GMetrixInput } from 'components/GMetrixInput/GMetrixInput';
import { SelectRoles } from 'components/SelectRoles/SelectRoles';
import { PartnerPermissions } from 'pages/partners/types';
import { FormattedMessage, useIntl } from 'react-intl';
import { useNotification } from 'app/notifications';
import { type UserPermissions } from 'pages/users';
import { User, UsersApi } from 'apis/UsersApi';
import { passMeetsRequirements } from 'utils';
import { useApp } from 'app';

export type UserEditDialogHandle = {
    hide: () => void;
    show: (user: User) => void;
}

type UserEditDialogProps = {
    onSaved?: (updatedUser: User) => void;
    partnerId: number;
    permissions: UserPermissions | PartnerPermissions;
}

export const UserEditDialog = forwardRef<UserEditDialogHandle, UserEditDialogProps>(({ onSaved, partnerId, permissions }, ref) => {

    const { userId } = useApp();
    const [showModal, setShowModal] = useState(false);
    const [user, setUser] = useState<User | null>(null);
    const [userOriginal, setUserOriginal] = useState<User | null>(null);
    const [newPermissionList, setNewPermissionList] = useState<string[]>([]);
    const [selectedTab, setSelectedTab] = useState(0);
    const [saving, setSaving] = useState(false);
    const [loadingUserData, setLoadingUserData] = useState(false);
    const [error, setError] = useState<string | null>(null);

    // Password state
    const [oldPassword, setOldPassword] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [confirmNewPassword, setConfirmNewPassword] = useState('');
    const [showOldPassword, setShowOldPassword] = useState(false);
    const [showNewPassword, setShowNewPassword] = useState(false);
    const [showConfirmNewPassword, setShowConfirmNewPassword] = useState(false);
    const [oldPasswordValid, setOldPasswordValid] = useState(true);
    const [newPasswordMeetsRequirements, setNewPasswordMeetsRequirements] = useState(true);
    const [passwordsMatch, setPasswordsMatch] = useState(true);
    const [showPasswordSuccessModal, setShowPasswordSuccessModal] = useState(false);
    const [resendingInvite, setResendingInvite] = useState(false);

    // Preferences state
    const [showResetPreferencesModal, setShowResetPreferencesModal] = useState(false);
    const [showResetToursModal, setShowResetToursModal] = useState(false);
    const [preferencesLoading, setPreferencesLoading] = useState(false);
    const [showConfirmDeleteModal, setShowConfirmDeleteModal] = useState(false);
    const [customTestsForModal, setCustomTestsForModal] = useState<{ data: string[]; loading: boolean }>({ data: [], loading: true });

    const { formatMessage } = useIntl();
    const { catchWithToast, createToast, onSuccessToast } = useNotification();

    const isCurrentUser = userId > 0 && user?.UserProfileID === userId;

    useImperativeHandle(ref, () => ({
        hide: () => handleClose(),
        show: (u: User) => {

            // Set basic user immediately so modal can render
            setUser({ ...u });
            setUserOriginal({ ...u });
            setNewPermissionList(u.Permissions?.map(p => p.PermissionId) ?? []);
            setSelectedTab(0);
            setError(null);
            setSaving(false);
            setLoadingUserData(true);
            resetPasswordState();
            setShowResetPreferencesModal(false);
            setShowResetToursModal(false);
            setPreferencesLoading(false);
            setShowConfirmDeleteModal(false);
            setCustomTestsForModal({ data: [], loading: true });
            setShowModal(true);

            // Fetch full user data (permissions + early access)
            Promise.all([
                UsersApi.GetPermissions(u.Id.toString(), partnerId).catch(() => [] as any[]),
                UsersApi.GetEarlyAccessPreference(u.Id).catch(() => null)
            ]).then(([userPermissions, earlyAccess]) => {

                const fullUser = {
                    ...u,
                    EarlyAccessPreference: earlyAccess ?? u.EarlyAccessPreference,
                    Permissions: userPermissions ?? u.Permissions ?? []
                };
                setUser(fullUser);
                setUserOriginal(fullUser);
                setNewPermissionList(fullUser.Permissions?.map(p => p.PermissionId) ?? []);

            }).finally(() => setLoadingUserData(false));

        }
    }));

    const handleClose = () => {

        setShowModal(false);
        setUser(null);
        setUserOriginal(null);
        setShowConfirmDeleteModal(false);
        setCustomTestsForModal({ data: [], loading: true });

    };

    const resetPasswordState = () => {

        setOldPassword('');
        setNewPassword('');
        setConfirmNewPassword('');
        setShowOldPassword(false);
        setShowNewPassword(false);
        setShowConfirmNewPassword(false);
        setOldPasswordValid(true);
        setNewPasswordMeetsRequirements(true);
        setPasswordsMatch(true);
        setShowPasswordSuccessModal(false);

    };

    const handleResendInvite = async () => {

        if (!user) return;

        try {

            setResendingInvite(true);
            const result = await UsersApi.ResendWelcomeEmail(user.Id);
            if (result?.Done) {

                onSuccessToast(result.Message || formatMessage({ id: 'welcome_email_resent.b71c2ee1', defaultMessage: 'Welcome email resent.' }));

            } else {

                createToast(result?.Message || formatMessage({ id: 'unable_to_resend_the_welcome_email.63ce3902', defaultMessage: 'Unable to resend the welcome email.' }), 'warning');

            }

        } catch (e) {

            catchWithToast(e, formatMessage({ id: 'failed_to_resend_the_welcome_email.62258af5', defaultMessage: 'Failed to resend the welcome email.' }));

        } finally {

            setResendingInvite(false);

        }

    };

    const editFullName = (value: string, firstOrLast: 'firstName' | 'lastName') => {

        if (!user) return '';
        const splitFullName = user.FullName?.split(' ');
        if (firstOrLast === 'firstName') {

            return `${value} ${splitFullName && splitFullName[splitFullName.length - 1]}`;

        }
        return `${splitFullName && splitFullName[0]} ${value}`;

    };

    const impersonateUser = (impersonateId: string) => {

        UsersApi.ImpersonateUser(impersonateId);

    };

    const handleConfirmDeleteModal = async () => {

        if (!user) return;

        try {

            const customTests = await UsersApi.GetUsersCustomTests(user.UserProfileID);
            setTimeout(() => {

                setCustomTestsForModal({ data: customTests, loading: false });

            }, 1000);

        } catch {

            // Failed to load custom tests

        }
        setShowConfirmDeleteModal(true);

    };

    const handleDeleteUser = async () => {

        if (!user) return;

        const response = await UsersApi.RemoveUserFromTC(user.UserProfileID, partnerId !== 256 ? partnerId : -1);
        if (response.Done) {

            createToast(formatMessage({ id: 'succesfully_removed_user_from_testing_ce.ce25d53d', defaultMessage: 'Succesfully removed user from Testing Center' }), 'success');
            handleClose();
            onSaved?.(user);

        } else {

            createToast(formatMessage({ id: 'failed_to_remove_user_from_testing_cente.b3368bfd', defaultMessage: 'Failed to remove user from Testing Center' }) + ' ' + response.Message, 'danger');

        }

    };

    const setActiveStatus = (value: string) => {

        if (!user) return;
        const isActiveBool = value === 'true';
        const updatedPartnerList = [...user.PartnerList];
        if (updatedPartnerList[0]) {

            updatedPartnerList[0] = { ...updatedPartnerList[0], Active: isActiveBool };

        }
        setUser({ ...user, PartnerList: updatedPartnerList });

    };

    const handleSave = async () => {

        if (!user) return;

        try {

            setSaving(true);
            setError(null);
            await UsersApi.Update(user, newPermissionList.toString(), partnerId);
            onSuccessToast(formatMessage({ id: 'successfully_updated.c1c0d6f2', defaultMessage: 'Successfully updated' }) || 'Successfully updated.');
            onSaved?.(user);
            handleClose();

        } catch (e: any) {

            setError(e?.message || String(e));
            catchWithToast(e, formatMessage({ id: 'failed_to_update.419f8ec3', defaultMessage: 'Failed to update' }) || 'Failed to update.');

        } finally {

            setSaving(false);

        }

    };

    // Password handlers
    const checkNewPasswordsMatch = () => {

        const match = newPassword.trim() === confirmNewPassword.trim();
        setPasswordsMatch(match);
        return match;

    };

    const handlePasswordReset = async (e: FormEvent) => {

        e.preventDefault();
        e.stopPropagation();

        if (!user) return;

        setOldPasswordValid(true);

        if (!newPasswordMeetsRequirements || !passwordsMatch) return;

        if (isCurrentUser) {

            const result = await UsersApi.ResetCurrentAdminPassword(oldPassword, newPassword);
            if (!result) {

                return setOldPasswordValid(false);

            }

        } else {

            const result = await UsersApi.ResetAdminPassword(user.Id, newPassword);
            if (!result) {

                return createToast(formatMessage({ id: 'password_reset_unsuccessfully.b693ad7f', defaultMessage: 'Password Reset Unsuccessfully' }), 'error');

            }

        }

        setShowPasswordSuccessModal(true);

    };

    // Preferences handlers
    const handleResetPreferences = async () => {

        try {

            setPreferencesLoading(true);
            await UsersApi.ResetPreferences();

        } catch (err) {

            console.error('Error resetting preferences:', err);

        } finally {

            setPreferencesLoading(false);

        }

    };

    const handleResetAllTours = async () => {

        try {

            setPreferencesLoading(true);
            await UsersApi.ResetAllTours();

        } catch (err) {

            console.error('Error resetting tours:', err);

        } finally {

            setPreferencesLoading(false);

        }

    };

    if (!user || !userOriginal) return null;

    return (
        <>
            <GMetrixDialog
                showCancelButton
                actionButtonClick={handleSave}
                actionButtonDisabled={saving || loadingUserData}
                actionButtonText={formatMessage({ id: 'save.d44082ae', defaultMessage: 'Save' }) || 'Save'}
                closeButtonText={formatMessage({ id: 'cancel.89a86adc', defaultMessage: 'Cancel' }) || 'Cancel'}
                dialogLabel={formatMessage({ id: 'edit_account.3a8dbb59', defaultMessage: 'Edit Account' }) || 'Edit Account'}
                onCancel={handleClose}
                onClose={handleClose}
                show={showModal}
                size="xl"
            >
                <Alert
                    dismissible
                    onClose={() => setError(null)}
                    show={!!error}
                    variant="danger"
                >
                    {error}
                </Alert>

                {loadingUserData ? (
                    <div className="p-5 text-center">
                        <GMetrixLoader />
                    </div>
                ) : (
                    <GMetrixTabs
                        onSelect={setSelectedTab}
                        selected={selectedTab}
                    >
                        {/* Tab 1: General Info */}
                        <GMetrixTabsTab
                            title={(
                                <div>
                                    {<FormattedMessage defaultMessage="General Info" id="general_info.d56ce80b" /> || 'General Info'}
                                </div>
                            )}
                        >
                            <div className="p-3">
                                <div className="mb-3">
                                    <p className="text-strong fw-semibold mb-2">
                                        {<FormattedMessage defaultMessage="Username/Email" id="usernameemail.9e110d3a" /> || 'Username / Email'}
                                    </p>
                                    <p>
                                        {user.UserName}
                                    </p>
                                </div>

                                <div className="mb-3 w-50">
                                    <GMetrixInput
                                        required
                                        onChange={e => setUser({
                                            ...user,
                                            FirstName: e.target.value,
                                            FullName: editFullName(e.target.value, 'firstName')
                                        })}
                                        defaultValue={userOriginal.FirstName}
                                        disabled={saving}
                                        label={formatMessage({ id: 'first_name.759a675b', defaultMessage: 'First Name' }) || 'First Name'}
                                        readOnly={!permissions.Edit || !permissions.UserEdit}
                                        size="sm"
                                    />
                                </div>

                                <div className="mb-3 w-50">
                                    <GMetrixInput
                                        required
                                        onChange={e => setUser({
                                            ...user,
                                            FullName: editFullName(e.target.value, 'lastName'),
                                            LastName: e.target.value
                                        })}
                                        defaultValue={userOriginal.LastName}
                                        disabled={saving}
                                        label={formatMessage({ id: 'last_name.029e7273', defaultMessage: 'Last Name' }) || 'Last Name'}
                                        readOnly={!permissions.Edit || !permissions.UserEdit}
                                        size="sm"
                                    />
                                </div>

                                {user.PartnerList?.[0] && !isCurrentUser && (
                                <div className="mb-3 w-50">
                                    {(() => {

                                        const statusOptions = [
                                            { id: 'true', text: formatMessage({ id: 'active.b9616c88', defaultMessage: 'Active' }) || 'Active' },
                                            { id: 'false', text: formatMessage({ id: 'inactive.5ee46a1c', defaultMessage: 'Inactive', description: 'New' }) || 'Inactive' }
                                        ];
                                        const currentStatusValue = user.PartnerList?.[0]?.Active?.toString();
                                        return (
                                            <GMetrixDropdown
                                                dataItemKey="id"
                                                dataItems={statusOptions}
                                                disabled={saving}
                                                filterable={false}
                                                label={formatMessage({ id: 'status.ad856ad7', defaultMessage: 'Status' }) || 'Status'}
                                                onChange={item => setActiveStatus((item as { id: string; text: string }).id)}
                                                textField="text"
                                                value={statusOptions.find(o => o.id === currentStatusValue)}
                                            />
                                        );

                                    })()}
                                </div>
                                )}

                                <div>
                                    <Form.Label className="fw-bold">
                                        {<FormattedMessage defaultMessage="Early Access" id="early_access.86082dfe" /> || 'Early Access'}
                                    </Form.Label>
                                    <GMetrixCheckInput
                                        checked={user.EarlyAccessPreference === 2}
                                        disabled={saving}
                                        id="editEarlyAccessNone"
                                        label={formatMessage({ id: 'none_default_to_standard_features_dont_s.1a693924', defaultMessage: 'None - Default to Standard features, don\'t show opt-in option.' }) || 'None'}
                                        name="EarlyAccess"
                                        onChange={() => setUser({ ...user, EarlyAccessPreference: 2 as any })}
                                        type="radio"
                                    />
                                    <GMetrixCheckInput
                                        checked={user.EarlyAccessPreference === 1}
                                        disabled={saving}
                                        id="editEarlyAccessOptIn"
                                        label={formatMessage({ id: 'opt_in_default_to_standard_features_show.76482b7c', defaultMessage: 'Opt-in - Default to Standard features, show opt-in option.' }) || 'Opt In'}
                                        name="EarlyAccess"
                                        onChange={() => setUser({ ...user, EarlyAccessPreference: 1 as any })}
                                        type="radio"
                                    />
                                    <GMetrixCheckInput
                                        checked={user.EarlyAccessPreference === 0}
                                        disabled={saving}
                                        id="editEarlyAccessAll"
                                        label={formatMessage({ id: 'all_default_to_early_access_features_sho.28e0b508', defaultMessage: 'All - Default to Early Access features, show opt-out option.' }) || 'All'}
                                        name="EarlyAccess"
                                        onChange={() => setUser({ ...user, EarlyAccessPreference: 0 as any })}
                                        type="radio"
                                    />
                                </div>

                                {!isCurrentUser && (
                                <Row className="mt-3">

                                    <Col className="my-3" xl={12}>
                                        <div className="select-roles__role d-flex align-items-center">
                                            <FormattedMessage defaultMessage="Actions" id="actions.61fd1d29" />
                                        </div>
                                    </Col>

                                    <Col className="my-2" xl={12}>
                                        <GMetrixButton
                                            className="m-2"
                                            disabled={!permissions.Authenticate || !permissions.Edit || !permissions.UserEdit}
                                            onClick={() => impersonateUser(user.Id)}
                                            variant="primary"
                                        >
                                            <FormattedMessage defaultMessage="Impersonate User" id="impersonate_user.54d49e91" />
                                        </GMetrixButton>
                                        <GMetrixButton
                                            className="m-2"
                                            disabled={resendingInvite || !permissions.Edit || !permissions.UserEdit}
                                            onClick={handleResendInvite}
                                            variant="primary"
                                        >
                                            <FormattedMessage defaultMessage="Resend Invite Email" id="resend_invite_email.56fa9cb5" />
                                        </GMetrixButton>
                                        <GMetrixButton
                                            className="m-2"
                                            disabled={!permissions.Edit || !permissions.UserEdit}
                                            onClick={handleConfirmDeleteModal}
                                            variant="danger"
                                        >
                                            <FormattedMessage defaultMessage="Remove User" id="remove_user.4584c8fe" />
                                        </GMetrixButton>
                                    </Col>

                                </Row>
                                )}
                            </div>
                        </GMetrixTabsTab>

                        {/* Tab 2: Permissions */}
                        <GMetrixTabsTab
                            title={(
                                <div>
                                    {<FormattedMessage defaultMessage="Permissions" id="permissions.076a2862" /> || 'Permissions'}
                                </div>
                            )}
                        >
                            <div className="p-3">
                                <SelectRoles
                                    externalSave
                                    handlerType="Update"
                                    newPermissionList={newPermissionList}
                                    partnerId={partnerId}
                                    permissions={permissions}
                                    setNewPermissionList={setNewPermissionList}
                                    user={user}
                                />
                            </div>
                        </GMetrixTabsTab>

                        {/* Tab 3: Password */}
                        <GMetrixTabsTab
                            title={(
                                <div>
                                    {<FormattedMessage defaultMessage="Password" description={formatMessage({ id: 'formulario.6dc35e9d', defaultMessage: 'Formulario' })} id="password.19467d6e" /> || 'Password'}
                                </div>
                            )}
                        >
                            <div className="p-3">
                                <h3 className="text-strong mb-3">
                                    {<FormattedMessage defaultMessage="Reset Password" id="reset_password.9c0200aa" /> || 'Reset Password'}
                                </h3>
                                <Form onSubmit={handlePasswordReset} style={{ width: 300 }}>
                                    {isCurrentUser && (
                                    <Form.Group className="mb-3" controlId="editOldPasswordInput">
                                        <InputGroup>
                                            <GMetrixInput
                                                required
                                                autoComplete="false"
                                                feedbackMessage={formatMessage({ id: 'password_is_incorrect.f558d2c0', defaultMessage: 'Password is incorrect' })}
                                                label={formatMessage({ id: 'old_password.98cdef1a', defaultMessage: 'Old Password' }) || 'Old Password'}
                                                labelClass="text-strong fw-semibold"
                                                onChange={e => setOldPassword(e.target.value)}
                                                placeholder={formatMessage({ id: 'current_password.04af60f8', defaultMessage: 'Current password' })}
                                                size="sm"
                                                type={showOldPassword ? 'text' : 'password'}
                                                validType={oldPasswordValid ? undefined : 'invalid'}
                                                value={oldPassword}
                                            />
                                            <GMetrixButton
                                                aria-label={showOldPassword ? formatMessage({ id: 'hide_old_password.a228aea5', defaultMessage: 'Hide old password', description: 'aria-label' }) : formatMessage({ id: 'show_old_password.36f22a35', defaultMessage: 'Show old password', description: 'aria-label' })}
                                                className="rounded-end-1 align-self-end"
                                                onClick={() => setShowOldPassword(!showOldPassword)}
                                                size="sm"
                                                tabIndex={-1}
                                                variant="secondary"
                                            >
                                                <i className={`fa-light ${showOldPassword ? 'fa-eye' : 'fa-eye-slash'}`}></i>
                                            </GMetrixButton>
                                        </InputGroup>
                                    </Form.Group>
                                    )}

                                    <Form.Group className="mb-3" controlId="editNewPasswordInput">
                                        <InputGroup>
                                            <GMetrixInput
                                                required
                                                autoComplete="false"
                                                feedbackMessage={formatMessage({ id: 'minimum_8_characters_at_least_one_upperc.02d5146b', defaultMessage: 'Minimum 8 characters, at least one uppercase letter, one lowercase letter, one number, and one special character.' }) || 'Password does not meet minimum requirements'}
                                                label={formatMessage({ id: 'new_password.bdda5f8f', defaultMessage: 'New Password' }) || 'New Password'}
                                                labelClass="text-strong fw-semibold"
                                                onBlur={e => setNewPasswordMeetsRequirements(passMeetsRequirements((e.target as HTMLInputElement).value))}
                                                onChange={e => setNewPassword(e.target.value)}
                                                size="sm"
                                                type={showNewPassword ? 'text' : 'password'}
                                                validType={newPasswordMeetsRequirements ? undefined : 'invalid'}
                                                value={newPassword}
                                            />
                                            <GMetrixButton
                                                aria-label={showNewPassword ? formatMessage({ id: 'hide_new_password.00da65f3', defaultMessage: 'Hide new password', description: 'aria-label' }) : formatMessage({ id: 'show_new_password.a873e606', defaultMessage: 'Show new password', description: 'aria-label' })}
                                                className="rounded-end-1 align-self-end"
                                                onClick={() => setShowNewPassword(!showNewPassword)}
                                                size="sm"
                                                tabIndex={-1}
                                                variant="secondary"
                                            >
                                                <i className={`fa-light ${showNewPassword ? 'fa-eye' : 'fa-eye-slash'}`}></i>
                                            </GMetrixButton>
                                        </InputGroup>
                                    </Form.Group>

                                    <Form.Group className="mb-3" controlId="editConfirmPasswordInput">
                                        <InputGroup>
                                            <GMetrixInput
                                                required
                                                autoComplete="false"
                                                feedbackMessage={formatMessage({ id: 'confirm_password_password_fields_must_ma.b3b7ad9a', defaultMessage: 'Confirm Password - Password fields must match.' }) || 'Passwords must match'}
                                                label={formatMessage({ id: 'confirm_password.e786fc3f', defaultMessage: 'Confirm Password' }) || 'Confirm Password'}
                                                labelClass="text-strong fw-semibold"
                                                onBlur={checkNewPasswordsMatch}
                                                onChange={e => setConfirmNewPassword(e.target.value)}
                                                placeholder={formatMessage({ id: 'confirm_password.e0b302e2', defaultMessage: 'Confirm password' })}
                                                size="sm"
                                                type={showConfirmNewPassword ? 'text' : 'password'}
                                                validType={passwordsMatch ? undefined : 'invalid'}
                                                value={confirmNewPassword}
                                            />
                                            <GMetrixButton
                                                aria-label={showConfirmNewPassword ? formatMessage({ id: 'hide_confirm_new_password.bbb8931a', defaultMessage: 'Hide confirm new password', description: 'aria-label' }) : formatMessage({ id: 'show_confirm_new_password.6b029183', defaultMessage: 'Show confirm new password', description: 'aria-label' })}
                                                className="rounded-end-1 align-self-end"
                                                onClick={() => setShowConfirmNewPassword(!showConfirmNewPassword)}
                                                size="sm"
                                                tabIndex={-1}
                                                variant="secondary"
                                            >
                                                <i className={`fa-light ${showConfirmNewPassword ? 'fa-eye' : 'fa-eye-slash'}`}></i>
                                            </GMetrixButton>
                                        </InputGroup>
                                    </Form.Group>

                                    <GMetrixButton type="submit" variant="primary">
                                        {<FormattedMessage defaultMessage="Reset Password" id="reset_password.9c0200aa" /> || 'Reset Password'}
                                    </GMetrixButton>
                                </Form>
                            </div>
                        </GMetrixTabsTab>

                        {/* Tab 4: Preferences */}
                        <GMetrixTabsTab
                            title={(
                                <div>
                                    {<FormattedMessage defaultMessage="Preferences" id="preferences.2bb53b2b" /> || 'Preferences'}
                                </div>
                            )}
                        >
                            <div className="p-3">
                                <h3 className="text-strong mb-3">
                                    {<FormattedMessage defaultMessage="Preferences" id="preferences.2bb53b2b" /> || 'Preferences'}
                                </h3>
                                <div className="p-4">
                                    <div className="mb-3">
                                        <div className="text-strong fw-semibold mb-2">
                                            {<FormattedMessage defaultMessage="Reset Site Preferences" id="reset_site_preferences.2c32a2c7" /> || 'Reset Site Preferences'}
                                        </div>
                                        <p className="text-muted mb-3">
                                            {<FormattedMessage defaultMessage="Resetting site preferences will restore all view and filter settings on tables to their default state." id="resetting_site_preferences_will_restore.ef3eae0e" /> || 'This will reset all site preferences to their defaults.'}
                                        </p>
                                        <GMetrixButton
                                            disabled={preferencesLoading}
                                            onClick={() => setShowResetPreferencesModal(true)}
                                            variant="outline-primary"
                                        >
                                            {<FormattedMessage defaultMessage="Reset Preferences" id="reset_preferences.3312b82d" /> || 'Reset Preferences'}
                                        </GMetrixButton>
                                    </div>

                                    <div>
                                        <div className="text-strong fw-semibold mb-2">
                                            {<FormattedMessage defaultMessage="Reset All On Screen Tours" id="reset_all_on_screen_tours.30deb8e9" /> || 'Reset All On-Screen Tours'}
                                        </div>
                                        <p className="text-muted mb-3">
                                            {<FormattedMessage defaultMessage="Restarting the on-screen tour replays the tutorial that introduces the user interface and key website features." id="restarting_the_on_screen_tour_replays_th.6c46b5f5" /> || 'This will reset all on-screen tours.'}
                                        </p>
                                        <GMetrixButton
                                            disabled={preferencesLoading}
                                            onClick={() => setShowResetToursModal(true)}
                                            variant="outline-primary"
                                        >
                                            {<FormattedMessage defaultMessage="Reset Tours" id="reset_tours.1ea7cc6c" /> || 'Reset Tours'}
                                        </GMetrixButton>
                                    </div>
                                </div>
                            </div>
                        </GMetrixTabsTab>
                    </GMetrixTabs>
                )}
            </GMetrixDialog>

            {/* Password success dialog */}
            <GMetrixDialog
                actionButtonClick={() => {

                    setShowPasswordSuccessModal(false);
                    resetPasswordState();

                }}
                onClose={() => {

                    setShowPasswordSuccessModal(false);
                    resetPasswordState();

                }}
                actionButtonDisabled={false}
                actionButtonText={formatMessage({ id: 'ok.981b7e92', defaultMessage: 'Ok' }) || 'OK'}
                dialogLabel={formatMessage({ id: 'reset_password.9c0200aa', defaultMessage: 'Reset Password' }) || 'Reset Password'}
                show={showPasswordSuccessModal}
                showCancelButton={false}
            >
                {<FormattedMessage defaultMessage="Password Reset Successfully" id="password_reset_successfully.4d5010e3" /> || 'Password reset successfully.'}
            </GMetrixDialog>

            {/* Reset Site Preferences confirm */}
            <GMetrixDialog
                showCancelButton
                actionButtonClick={async () => {

                    await handleResetPreferences();
                    setShowResetPreferencesModal(false);

                }}
                actionButtonDisabled={false}
                actionButtonText={formatMessage({ id: 'reset.43ed980c', defaultMessage: 'Reset' }) || 'Reset'}
                closeButtonText={formatMessage({ id: 'cancel.89a86adc', defaultMessage: 'Cancel' }) || 'Cancel'}
                dialogLabel={formatMessage({ id: 'reset_site_preferences.2c32a2c7', defaultMessage: 'Reset Site Preferences' }) || 'Reset Site Preferences'}
                onCancel={() => setShowResetPreferencesModal(false)}
                show={showResetPreferencesModal}
            >
                <Container>
                    <Row>
                        <Col>
                            <p className="text-muted">
                                {<FormattedMessage defaultMessage="Are you sure you want to reset site preferences?" id="are_you_sure_you_want_to_reset_site_pref.caf40b80" /> || 'Are you sure you want to reset your site preferences?'}
                            </p>
                        </Col>
                    </Row>
                </Container>
            </GMetrixDialog>

            {/* Reset All Tours confirm */}
            <GMetrixDialog
                showCancelButton
                actionButtonClick={async () => {

                    await handleResetAllTours();
                    setShowResetToursModal(false);

                }}
                actionButtonDisabled={false}
                actionButtonText={formatMessage({ id: 'reset.43ed980c', defaultMessage: 'Reset' }) || 'Reset'}
                closeButtonText={formatMessage({ id: 'cancel.89a86adc', defaultMessage: 'Cancel' }) || 'Cancel'}
                dialogLabel={formatMessage({ id: 'reset_all_on_screen_tours.30deb8e9', defaultMessage: 'Reset All On Screen Tours' }) || 'Reset All On-Screen Tours'}
                onCancel={() => setShowResetToursModal(false)}
                show={showResetToursModal}
            >
                <Container>
                    <Row>
                        <Col>
                            <p className="text-muted">
                                {<FormattedMessage defaultMessage="Are you sure you want to reset all on-screen tours?" id="are_you_sure_you_want_to_reset_all_on_sc.223f3f9b" /> || 'Are you sure you want to reset all on-screen tours?'}
                            </p>
                        </Col>
                    </Row>
                </Container>
            </GMetrixDialog>

            {/* Remove user confirm */}
            <GMetrixDialog
                actionButtonClick={() => {

                    setCustomTestsForModal({ data: [], loading: true });
                    setShowConfirmDeleteModal(false);
                    handleDeleteUser();

                }}
                onCancel={() => {

                    setCustomTestsForModal({ data: [], loading: true });
                    setShowConfirmDeleteModal(false);

                }}
                actionButtonDisabled={false}
                actionButtonText={formatMessage({ id: 'yes.104886cd', defaultMessage: 'Yes', description: 'Formulario' })}
                closeButtonText={formatMessage({ id: 'no.4dd074da', defaultMessage: 'No', description: 'Formulario' })}
                dialogLabel={formatMessage({ id: 'delete_user_from_testing_center.872f1723', defaultMessage: 'Delete User From Testing Center' })}
                show={showConfirmDeleteModal}
            >
                <Container>

                    <Row>
                        <Col className="kendo-dialog-text-only">
                            <FormattedMessage defaultMessage="Deleting this user from your testing center will also remove all of their custom tests. Would you like to process?" id="deleting_this_user_from_your_testing_cen.fa120f29" />
                        </Col>
                    </Row>

                    <Row className="mb-3">
                        <Col className="col-12 mb-3">
                            <h2>
                                <FormattedMessage defaultMessage="Custom Tests" id="custom_tests.9e7494ea" />
                                {' '}
                                {!customTestsForModal.loading && `(${customTestsForModal.data.length})`}
                            </h2>
                        </Col>

                        {!customTestsForModal.loading ? (
                            <Col className="details__custom_test_container">
                                {customTestsForModal.data.length > 0 ? (
                                    <>
                                        {customTestsForModal.data.map((customTestName, i) => (
                                            <Col className="col-12 details__custom_test_item" key={`${i}_${customTestName}`}>
                                                {customTestName}
                                            </Col>
                                        ))}
                                    </>
                                ) : (
                                    <Col className="col-12 details__custom_test_item d-flex justify-content-center">
                                        <FormattedMessage defaultMessage="No Custom Test Records" id="no_custom_test_records.1a723aa5" />
                                    </Col>
                                )}
                            </Col>
                        ) : (
                            <Col className="col-12 d-flex justify-content-center my-2 details__custom_test_item">
                                <GMetrixLoader />
                            </Col>
                        )}
                    </Row>
                </Container>
            </GMetrixDialog>
        </>
    );

}
);

UserEditDialog.displayName = 'UserEditDialog';

export default UserEditDialog;
